import {
  MultipleSearchParams,
  PlainJSONSearchParams,
  RangeSearchParams,
  SearchModality,
  SearchParams,
  SearchType,
  SearchValue,
  SingleSearchParams,
} from '../../types';

// get the correct format for a search modality - '1234 - 1235' or '1234'
export const formatSearchModality = (searchParams?: SearchParams | null) => {
  if (searchParams) {
    if (searchParams.searchModality === 'range') return `${searchParams?.fromValue} - ${searchParams?.toValue}`;
    if (searchParams.searchModality === 'multiple') return searchParams.searchValue.join(', ');
    if (!searchParams?.searchValue) return '';
    return searchParams?.searchValue?.toString();
  }
};

export const formatForValidOmloop = (query: string, userFriendly: boolean = false) => {
  return query
    .trim()
    .split(/[\s,;]+/)
    .filter((s) => s.trim().length > 0)
    .map((s) => {
      const match = s.match(/^([a-zA-Z]+)(\d*)$/);
      if (match) {
        if (match[2]) return `${match[1].toUpperCase()}-${match[2]}`;
        return userFriendly ? `${match[1].toUpperCase()}` : `${match[1].toUpperCase()}*`;
      }
      return s.toUpperCase();
    });
};

export const parseValidRange = (query: string) => {
  const matches = query.match(/^(\d+)-(\d+)$/);
  if (matches && matches.length > 2) {
    const [__, fromValue, toValue] = matches.map((v) => Number(v));
    if (toValue > fromValue) {
      return [fromValue, toValue];
    }
    return null;
  }
};

export const parseValidEnumeration = (value: string) => {
  const validEnumerationRegex = /^(\d{1,14}[\s,;]+)+\d{1,14}[\s,;]*$/; // NOSONAR
  if (validEnumerationRegex.test(value)) {
    return value
      .split(/[\s,;]+/)
      .filter((s) => s.trim().length > 0)
      .map((s) => Number(s));
  }
  return null;
};

export const validateJson = (query: string) => {
  try {
    JSON.parse(query);
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

/**
 * Sanitize the given input.
 * @param query
 * @returns
 */
export function sanitizeInput(
  query: string,
  searchType: SearchType,
): {
  preserveSpace: boolean;
  /**
   * Seperator for reformatting the query
   * @default ', '
   */
  seperator: ', ' | ' ';
  input: string;
  hasLength: boolean;
  shouldValidate: boolean;
  searchValues: string[];
} {
  let seperator: ' ' | ', ' = ', ';
  const preserveSpace = query.endsWith(' ');
  // Changes query.replaceAll(' ', '') to (' ', ','),
  // but this can be a problem for materieelsoorten met een spatie erin, but its an edge case for later.
  let input = query
    .trim()
    .replaceAll(/\s?-\s?/gm, '-') // Format ranges
    .replaceAll(/[,;.]\s?/gm, ',') // Uniform seperator
    .toUpperCase();

  if (input.includes(' ') && !input.includes(',')) seperator = ' ';

  input = input.replaceAll(' ', ',');

  if (searchType === 'json') input = query;
  const hasLength = query.length > 0;
  const shouldValidate = ![';', ',', '-', '*'].some((end) => input.endsWith(end)) && !preserveSpace && hasLength;
  return {
    preserveSpace,
    seperator,
    input,
    hasLength,
    shouldValidate,

    // The plan is to (eventually) cut out searchModality,
    // therefor assume that all values are multiple and 1 of multiple could be a range value.
    searchValues: input.split(','),
  };
}

export type ValidateSearchQueryProps = {
  query: string;
  searchType: SearchType;
  isWithinAdvancedSearch: boolean;
};
/** Validate the searchQuery input. */
export function validateSearchQuery({ query, searchType, isWithinAdvancedSearch: __ }: ValidateSearchQueryProps) {
  const {
    preserveSpace,
    input,
    hasLength,
    shouldValidate,
    seperator,
    searchValues: ___,
  } = sanitizeInput(query, searchType);
  // Identify the searchQuery and validate its contents.
  // Only tests numeric values for searchTypes: 'train, rollingStock, emtpy'
  const isValidEmpty = searchType === 'empty' && /[\s\w-,;+]{1,14}/gm.test(input);
  const isValidSingle = /^\d{1,14}$/.test(input);

  // Depending on the searchType:
  // searchType !== empty: Only tests numeric values for searchTypes: 'train, rollingStock'
  const regexpMultipleNumeric = /^(\d{1,14}[\s,;]+)+\d{1,14}[\s,;]*$/.test(input);
  const regexpMultipleOther = /^([\w-+*]{1,14}[\s,;]+)+[\w-+*]{1,14}[\s,;]*$/.test(input);

  const isValidMultiple =
    (['train', 'rollingStock', 'empty'].includes(searchType) && regexpMultipleNumeric) ||
    (!['train', 'rollingStockType'].includes(searchType) && regexpMultipleOther);

  const regexpNumericRange = /^\d{1,14}\s?-\s?\d{1,14}$/.test(input);
  const isValidRange = ['train', 'rollingStock', 'empty'].includes(searchType) && regexpNumericRange;
  // && parseValidRange(input);

  const isValidJson = searchType === 'json' && hasLength && validateJson(input);
  const isValidMaterieelTypeValue = searchType === 'rollingStockType' && hasLength && /^[\w,;\-\s+]+$/.test(input);
  const isValidOmloopValue =
    searchType === 'omloop' && hasLength && /^([a-zA-Z]{2,3}\*?-?\d*[\s,;:]*)*[a-zA-Z]{2,3}\*?-?\d*$/.test(input);

  const isValidSingleMultipleOrRangeValue = isValidSingle || isValidMultiple || isValidRange;

  const isValidAlpha = ['claimsoort', 'bijzonderheidscode'].includes(searchType) && /([A-Za-z]+,? ?)/gm.test(input);

  /** Interpret the validations and check for errors. */
  const hasValidationError = () => {
    if (!hasLength) return false; // Defaults to no error.
    return [
      isValidSingle,
      isValidMultiple,
      isValidRange,
      isValidJson,
      isValidMaterieelTypeValue,
      isValidOmloopValue,
      isValidEmpty,
      isValidAlpha,
    ].every((val) => val === false);
  };
  const hasError = hasValidationError();

  /** Determine the searchModality */
  const getSearchModality = (): SearchModality => {
    if (isValidJson) return 'json';

    // The first validation to check if it is a range, will only validate true if the range values are valid.
    if (
      isValidRange ||
      (['train', 'rollingStock', 'empty'].includes(searchType) && /^\d+-\d?$/gm.test(input) && input.includes('-'))
    )
      return 'range';
    // isValidMaterieelTypeValue and isValidOmloopValue are added for backwards compatibility
    if (isValidMultiple || isValidMaterieelTypeValue || isValidOmloopValue) return 'multiple';
    return 'single';
  };
  const searchModality = getSearchModality();

  /** Get the 'searchValue' (single | multiple | json) or 'fromValue' and 'toValue' (range) */
  const getSearchValues = () => {
    const values = {
      searchValue: null,
      fromValue: null,
      toValue: null,
    } as {
      searchValue: SearchValue | null;
      fromValue: number | null;
      toValue: number | null;
    };
    if (isValidRange) {
      const rangeValues = parseValidRange(input);
      values.fromValue = rangeValues?.[0] ?? null;
      values.toValue = rangeValues?.[1] ?? null;
    } else {
      if (isValidEmpty) values.searchValue = input;
      if (isValidAlpha) values.searchValue = input.toUpperCase();
      if (isValidSingle) values.searchValue = Number(input);
      if (isValidMultiple) values.searchValue = parseValidEnumeration(input) ?? input.split(',');
      if (isValidJson) values.searchValue = query;
      if (isValidMaterieelTypeValue)
        values.searchValue = input
          .trim()
          .split(/[,;]/)
          .map((val) => val.trim())
          .filter((s) => s.length > 0);

      if (isValidOmloopValue) values.searchValue = formatForValidOmloop(query);
    }
    return values;
  };
  const { searchValue, fromValue, toValue } = getSearchValues();

  const createSearchParams = ({ searchDate = null }: { searchDate?: string | null }): SearchParams | undefined => {
    if (hasError) return;
    const searchParams: Partial<SearchParams> = {
      searchModality,
      searchType,
    };
    if (searchModality !== 'json') {
      searchParams.searchDate = searchDate ?? null;
      if (searchDate) {
        const descriptionDate = searchDate.split('-');
        descriptionDate.reverse();
        searchParams.description = ` op ${descriptionDate.join('-')}`;
      } else searchParams.description = ' ';
    }
    if (searchModality === 'range' && isValidRange && fromValue && toValue) {
      (searchParams as RangeSearchParams).fromValue = fromValue;
      (searchParams as RangeSearchParams).toValue = toValue;
    } else if (searchValue !== null) {
      (searchParams as SingleSearchParams | MultipleSearchParams | PlainJSONSearchParams).searchValue = searchValue;
    }
    return searchParams as SearchParams;
  };

  // Utility to format the searchValue or fromValue - toValue
  // The final result of the formattedValue should be set to query.
  const formatSearchValue = (): string => {
    const trailingSpace = preserveSpace ? ' ' : '';
    if (searchModality === 'json') return query;
    if (!shouldValidate) {
      if (searchModality === 'range') return `${input.replace(/\s?-/, ' -')}${trailingSpace}`;
      if (searchModality === 'multiple') return `${input.replaceAll(/,\s?/gm, seperator).trim()}${trailingSpace}`;
      return `${input}${trailingSpace}`;
    }
    if (isValidOmloopValue) return formatForValidOmloop(query, true).join(seperator);
    if (searchModality === 'range') return `${input.replace(/\s?-\s?/, ' - ')}`;
    if (Array.isArray(searchValue) && searchValue.length && searchModality === 'multiple') {
      if (searchValue.length === 1) {
        // Fix for validating omloop
        return input.startsWith(String(searchValue[0])) ? searchValue.join(seperator) : input;
      }
      return searchValue.join(seperator);
    }
    if (searchValue) {
      if (searchType !== 'omloop') return `${String(searchValue)}${trailingSpace}`;
    }
    return `${query}${trailingSpace}`;
  };

  /**
   * Get the begintMet value to be used in the autocompleteSearchQuery.
   */
  const getBegintMet = (): string | null => {
    let _begintMet = input;
    if (shouldValidate) {
      if (searchModality === 'multiple' && Array.isArray(searchValue) && (searchValue as (number | string)[]).length)
        _begintMet = String(searchValue[searchValue.length - 1]).trim();
      if (searchModality === 'range') {
        // Save to assume that its the toValue when it validates to range, the first value is always interpreted as 'single'
        const ranges = input.split('-').map((inp) => inp.trim());
        _begintMet = String(ranges?.[1] ?? ranges?.[0] ?? '');
      }
      // Replace auto postfixed wildcard search (until now only used for searchType omloop)
      return _begintMet.replace('*', '');
    }
    return null;
  };

  const formattedInput = formatSearchValue();
  const begintMet = getBegintMet();

  return {
    createSearchParams,
    fromValue,
    hasError,
    isValid: !hasError,
    isValidMaterieelTypeValue,
    isValidOmloopValue,
    isValidSingleMultipleOrRangeValue,
    searchModality,
    searchValue,
    toValue,
    formattedInput,
    shouldValidate,
    begintMet,
  };
}
